import { ComponentProps, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '~/helpers/tw';
import { sendNotification, SendNotificationOptions } from '../toast';
import { Tooltip } from '../tooltip';

type Variant = 'default' | 'primary' | 'secondary' | 'error' | 'link' | 'ghost';

type Size = 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends ComponentProps<'button'> {
    variant?: Variant;
    size?: Size;
    tooltip?: boolean;
    icon?: {
        position: 'start' | 'end';
        component: React.ReactNode;
    };
    tooltipProps?: ComponentProps<typeof Tooltip>;
    disabledToastOptions?: SendNotificationOptions;
}

interface GetButtonClassNameProps {
    variant: Variant;
    size?: Size;
    disabled?: boolean;
}

export const getButtonClassName = ({
    variant,
    size = 'sm',
    disabled = false,
}: GetButtonClassNameProps) =>
    cn(
        'leading-0 btn gap-1 rounded-md border px-3 text-sm font-normal flex-nowrap whitespace-nowrap',
        {
            'btn-xs text-xs': size === 'xs',
            'btn-sm': size === 'sm',
            'btn-md': size === 'md',
            'btn-lg': size === 'lg',
            'btn-ghost border-none p-1 hover:bg-transparent/10':
                variant === 'ghost',
            'disabled:hover:bg-transparent': variant === 'ghost',
            'cursor-default hover:bg-transparent':
                variant === 'ghost' && disabled,
            'border-primary bg-primary text-white hover:text-white hover:border-primary/70 hover:bg-primary/70':
                variant === 'primary',
            'dark:border-blue-500 dark:bg-blue-500 dark:bg-blue-500/80 dark:text-white':
                variant === 'primary',
            'disabled:bg-blue-200 disabled:text-blue-50 dark:disabled:bg-blue-800 dark:disabled:text-gray-600':
                variant === 'primary',
            'bg-blue-200 text-blue-50 hover:text-blue-50 border-blue-200 hover:border-blue-200 hover:bg-blue-200 cursor-default dark:border-blue-800 dark:hover:border-blue-800 dark:bg-blue-800 dark:hover:bg-blue-800 dark:text-gray-600 dark:hover:text-gray-600':
                variant === 'primary' && disabled,
            'border-gray-800 bg-gray-800 text-white hover:bg-gray-800/70':
                variant === 'secondary',
            'dark:border-gray-950 dark:bg-gray-950 dark:hover:border-gray-950 dark:hover:bg-gray-950/70':
                variant === 'secondary',
            'disabled:border-gray-500 disabled:bg-gray-500 disabled:text-gray-200 dark:disabled:border-gray-600 dark:disabled:bg-gray-600 dark:disabled:text-gray-400':
                variant === 'secondary',
            'border-gray-500 bg-gray-500 hover:bg-gray-500 text-gray-200 dark:border-gray-600 dark:bg-gray-600 dark:hover;bg-gray-600 cursor-default dark:text-gray-400':
                variant === 'secondary' && disabled,
            'border-gray-500 bg-transparent text-gray-950 hover:border-gray-500/60 hover:bg-transparent hover:text-gray-950/60':
                variant === 'default',
            'dark:border-gray-700 dark:text-white dark:hover:border-gray-700/60 dark:hover:bg-transparent dark:hover:text-white/70':
                variant === 'default',
            'disabled:bg-transparent disabled:border-gray-200 disabled:text-gray-300 dark:disabled:bg-transparent dark:disabled:border-gray-600 dark:disabled:text-gray-500':
                variant === 'default',
            'bg-transparent border-gray-200 text-gray-300 hover:text-gray-300 hover:bg-transparent dark:hover:bg-transparent cursor-default dark:bg-transparent dark:border-gray-600 dark:text-gray-500 dark:hover:text-gray-500':
                variant === 'default' && disabled,
            'border-error-darker dark:border-error bg-error-darker text-white hover:bg-error-darker/70 dark:bg-error dark:hover:bg-error/70 dark:text-white':
                variant === 'error',
            'btn-link max-w-full truncate bg-transparent p-0 text-primary underline hover:text-primary/70 dark:text-blue-200 dark:hover:text-blue-200/60':
                variant === 'link',
            'disabled:text-primary/70 disabled:bg-transparent peer-disabled:bg-transparent peer-disabled:text-primary/70 disabled:hover:text-primary/70 dark:disabled:text-gray-600 dark:peer-disabled:text-gray-700 dark:disabled:hover:text-gray-600':
                variant === 'link',
            'text-primary/70 bg-transparent bg-transparent hover:text-primary/70 cursor-default dark:text-gray-600 dark:hover:text-gray-600':
                variant === 'link' && disabled,
        }
    );

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            children,
            variant = 'default',
            size = 'sm',
            tooltip = false,
            icon,
            // Use disabled as a prop to avoid passing it to the button element
            // which would make it non-interactive
            disabled,
            onClick,
            tooltipProps,
            disabledToastOptions,
            ...props
        },
        ref
    ) => {
        const { t } = useTranslation();
        return (
            <button
                {...props}
                ref={ref}
                className={cn(
                    getButtonClassName({ variant, size, disabled }),
                    className
                )}
                onClick={(e) => {
                    // If the button is disabled, show a notification and do not call onClick
                    if (disabled) {
                        sendNotification(
                            disabledToastOptions ?? {
                                toastId: 'no-permissions',
                                type: 'warning',
                                data: {
                                    title: t('error.noPermission'),
                                },
                            }
                        );
                        return;
                    }
                    onClick?.(e);
                }}
            >
                {icon?.position === 'start' && icon.component}
                {tooltip ? (
                    <Tooltip
                        position="top"
                        content={children}
                        className="truncate"
                        {...tooltipProps}
                    >
                        {children}
                    </Tooltip>
                ) : (
                    children
                )}
                {icon?.position === 'end' && icon.component}
            </button>
        );
    }
);

Button.displayName = 'Button';

export default Button;
